import { useState, useEffect } from 'react';
import { registerAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Toaster, toast } from 'sonner';
import { 
  ClipboardCheck, Search, UserCheck, UserX, Clock, Users, 
  CheckCircle, XCircle, RefreshCw, Calendar, LogIn, LogOut, Undo2, AlertTriangle,
  ChevronRight, Lock, CalendarX
} from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';

// IMPORTANT: 0=Sunday, 6=Saturday (JavaScript Date standard)
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BulkActionBar = ({ count, total, allSelected, onToggleAll, onStageIn, onStageOut }) => (
  <div className="bg-white rounded-xl border border-neutral-200 p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={allSelected}
        onChange={onToggleAll}
        className="w-6 h-6 accent-purple-600 cursor-pointer"
        data-testid="select-all-checkbox"
      />
      <span className="text-sm text-neutral-700 font-medium">Select All ({total})</span>
    </label>
    {count > 0 && (
      <>
        <Badge className="bg-purple-100 text-purple-700 border-purple-200">{count} selected</Badge>
        <div className="flex gap-2 ml-auto flex-wrap">
          <Button size="sm" onClick={onStageIn} className="bg-green-500 hover:bg-green-600" data-testid="bulk-sign-in-btn">
            <LogIn className="w-4 h-4 mr-1" /> Sign In Selected
          </Button>
          <Button size="sm" onClick={onStageOut} className="bg-blue-500 hover:bg-blue-600" data-testid="bulk-sign-out-btn">
            <LogOut className="w-4 h-4 mr-1" /> Sign Out Selected
          </Button>
        </div>
      </>
    )}
  </div>
);

const Register = () => {
  const { user } = useAuth();
  const [selectedDay, setSelectedDay] = useState(null); // null = today's register, string = day name
  const [viewMode, setViewMode] = useState('master'); // 'master' = unique students, 'classes' = by class
  const [registerData, setRegisterData] = useState(null);
  const [dayRegisterData, setDayRegisterData] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [lateModalOpen, setLateModalOpen] = useState(null);
  const [lateTime, setLateTime] = useState('');
  const [expandedClass, setExpandedClass] = useState(null);
  const [markingAttendance, setMarkingAttendance] = useState(null);
  const [dayIsClosed, setDayIsClosed] = useState(false);
  const [registerDate, setRegisterDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedByClass, setSelectedByClass] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  useEffect(() => {
    fetchAvailableDays();
    fetchRegister();
  }, []);

  useEffect(() => {
    if (selectedDay) {
      fetchDayRegister(selectedDay);
    }
  }, [selectedDay]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedByClass({});
  }, [selectedDay, viewMode]);

  const fetchAvailableDays = async () => {
    try {
      // Fetch schedules to get the days classes are offered
      const res = await api.get('/schedules');
      const days = new Set();
      // Schedules store day_of_week in JS convention (Sun=0..Sat=6),
      // matching the admin create-schedule dropdown (DAYS_OF_WEEK index).
      const dayMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      res.data.forEach(schedule => {
        const dayNum = schedule.day_of_week;
        if (dayNum !== undefined && dayNum !== null && dayNum >= 0 && dayNum <= 6) {
          days.add(dayMap[dayNum]);
        }
      });
      
      // Sort days in correct order (Sunday first to match DAYS_OF_WEEK)
      setAvailableDays(DAYS_OF_WEEK.filter(d => days.has(d)));
    } catch (error) {
      console.error('Failed to fetch days:', error);
      // Fallback: show all weekdays
      setAvailableDays(['Saturday']);
    }
  };

  const fetchRegister = async () => {
    setLoading(true);
    try {
      const res = await registerAPI.getToday();
      setRegisterData(res.data);
      // Check if all students are accounted for
      if (res.data?.students) {
        checkAutoClose(res.data.students);
      }
    } catch (error) {
      toast.error('Failed to load register');
    } finally {
      setLoading(false);
    }
  };

  const fetchDayRegister = async (day) => {
    setLoading(true);
    try {
      const res = await api.get(`/register/day/${day}`);
      setDayRegisterData(res.data);
      // Check if all students are accounted for
      if (res.data?.classes) {
        const allStudents = res.data.classes.flatMap(c => c.students || []);
        checkAutoClose(allStudents);
      }
    } catch (error) {
      toast.error('Failed to load day register');
      setDayRegisterData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async (classId, bookingId, action) => {
    setMarkingAttendance(`${classId}-${bookingId}`);
    try {
      const res = await api.post(`/register/class/${classId}/mark`, {
        booking_id: bookingId,
        action: action,
        date: new Date().toISOString().split('T')[0]
      });
      const msgs = { sign_in: 'Signed in', sign_out: 'Signed out', absent: 'Marked as absent', clear: 'Cleared' };
      toast.success(msgs[action] || 'Updated');
      applyLocalClassMark(classId, bookingId, action, res.data || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setMarkingAttendance(null);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  const handleCloseDay = async () => {
    if (!window.confirm('Are you sure you want to close the day? This will mark all remaining students as signed out.')) {
      return;
    }
    
    setLoading(true);
    try {
      // Sign out all students who are still signed in — one bulk request
      const signedInStudents = allStudents.filter(s => s.signed_in && !s.signed_out);
      if (signedInStudents.length) {
        await api.post('/register/bulk-mark', {
          changes: signedInStudents.map(s => ({ booking_id: s.booking_id, action: 'sign_out' })),
        });
      }
      setDayIsClosed(true);
      toast.success('Day closed successfully! All students signed out.');
      fetchRegister();
    } catch (error) {
      toast.error('Failed to close day');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (bookingId, isLate = false, arrivalTime = null) => {
    setActionLoading(bookingId);
    try {
      await registerAPI.signIn(bookingId, isLate, arrivalTime);
      toast.success(isLate ? 'Student signed in (Late)' : 'Student signed in');
      setLateModalOpen(null);
      setLateTime('');
      applyLocalMark([bookingId], 'sign_in', { is_late: isLate, arrival_time: arrivalTime });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign in');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkLate = async (bookingId) => {
    if (!lateTime) {
      toast.error('Please enter arrival time');
      return;
    }
    setActionLoading(bookingId);
    try {
      await registerAPI.markLate(bookingId, lateTime);
      toast.success('Marked as late');
      setLateModalOpen(null);
      setLateTime('');
      fetchRegister();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as late');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSignInLate = async (bookingId) => {
    if (!lateTime) {
      toast.error('Please enter arrival time');
      return;
    }
    await handleSignIn(bookingId, true, lateTime);
  };

  const handleSignOut = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await registerAPI.signOut(bookingId);
      toast.success('Student signed out');
      applyLocalMark([bookingId], 'sign_out');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign out');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUndoSignIn = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await registerAPI.undoSignIn(bookingId);
      toast.success('Sign-in removed');
      applyLocalMark([bookingId], 'clear');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo sign-in');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return '';
    }
  };

  // Check if all students are accounted for (signed out, absent, or no show) and auto-close
  const checkAutoClose = (students) => {
    if (!students || students.length === 0) return;
    
    const allAccountedFor = students.every(s => 
      s.status === 'signed_out' || 
      s.status === 'absent' || 
      s.status === 'no_show'
    );
    
    if (allAccountedFor && !dayIsClosed) {
      setDayIsClosed(true);
      toast.success('All students accounted for - Day automatically closed!', {
        icon: '✅',
        duration: 4000
      });
    }
  };

  // Get today's day name
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long' });

  // Filter students for today's register
  const filteredStudents = registerData?.students?.filter(s => {
    const matchesSearch = s.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.class_names.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;
    
    switch (statusFilter) {
      case 'signed_in':
        return s.signed_in && !s.signed_out;
      case 'signed_out':
        return s.signed_out;
      case 'not_arrived':
        return !s.signed_in;
      case 'late':
        return s.is_late;
      default:
        return true;
    }
  }) || [];

  const allStudents = registerData?.students || [];
  const signedInCount = allStudents.filter(s => s.signed_in && !s.signed_out).length;
  const signedOutCount = allStudents.filter(s => s.signed_out).length;
  const notArrivedCount = allStudents.filter(s => !s.signed_in).length;
  const lateCount = allStudents.filter(s => s.is_late).length;
  const totalCount = registerData?.total || 0;

  // Get unique students for Master Register (deduplicated by student name)
  const getUniqueStudents = () => {
    const studentMap = new Map();
    
    allStudents.forEach(s => {
      const key = s.student_name?.toLowerCase().trim();
      if (!key) return;
      
      if (!studentMap.has(key)) {
        studentMap.set(key, {
          student_name: s.student_name,
          student_id: s.student_id,
          booking_ids: [s.booking_id],
          class_names: [...(s.class_names || [])],
          signed_in: s.signed_in,
          signed_in_at: s.signed_in_at,
          signed_out: s.signed_out,
          signed_out_at: s.signed_out_at,
          is_late: s.is_late,
          arrival_time: s.arrival_time,
          is_absent_reported: s.is_absent_reported,
          is_trial: !!s.is_trial,
          trial_id: s.trial_id,
          is_payg: !!s.is_payg,
          bundle_id: s.bundle_id,
          credits_remaining: s.credits_remaining,
          credits_total: s.credits_total,
          // Use first booking for sign in/out actions
          primary_booking_id: s.booking_id
        });
      } else {
        // Merge with existing student
        const existing = studentMap.get(key);
        existing.booking_ids.push(s.booking_id);
        if (s.is_trial) {
          existing.is_trial = true;
          existing.trial_id = existing.trial_id || s.trial_id;
        }
        if (s.is_payg) {
          existing.is_payg = true;
          existing.bundle_id = existing.bundle_id || s.bundle_id;
          existing.credits_remaining = s.credits_remaining ?? existing.credits_remaining;
          existing.credits_total = s.credits_total ?? existing.credits_total;
        }
        // Add unique class names
        (s.class_names || []).forEach(cls => {
          if (!existing.class_names.includes(cls)) {
            existing.class_names.push(cls);
          }
        });
        // If any booking is signed in, student is signed in
        if (s.signed_in) {
          existing.signed_in = true;
          existing.signed_in_at = existing.signed_in_at || s.signed_in_at;
        }
        // If any booking is signed out, student is signed out
        if (s.signed_out) {
          existing.signed_out = true;
          existing.signed_out_at = existing.signed_out_at || s.signed_out_at;
        }
        if (s.is_late) {
          existing.is_late = true;
          existing.arrival_time = existing.arrival_time || s.arrival_time;
        }
        if (s.is_absent_reported) {
          existing.is_absent_reported = true;
        }
      }
    });
    
    return Array.from(studentMap.values());
  };

  const uniqueStudents = getUniqueStudents();
  const uniqueSignedIn = uniqueStudents.filter(s => s.signed_in && !s.signed_out).length;
  const uniqueSignedOut = uniqueStudents.filter(s => s.signed_out).length;
  const uniqueNotArrived = uniqueStudents.filter(s => !s.signed_in).length;

  // Filter unique students for Master Register
  const filteredUniqueStudents = uniqueStudents.filter(s => {
    const matchesSearch = s.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.class_names.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;
    
    switch (statusFilter) {
      case 'signed_in':
        return s.signed_in && !s.signed_out;
      case 'signed_out':
        return s.signed_out;
      case 'not_arrived':
        return !s.signed_in;
      case 'late':
        return s.is_late;
      default:
        return true;
    }
  });

  // Sign in all bookings for a student (for master register)
  const handleSignInAll = async (student, isLate = false, arrivalTime = null) => {
    setActionLoading(student.primary_booking_id);
    try {
      // Sign in all bookings for this student
      for (const bookingId of student.booking_ids) {
        await registerAPI.signIn(bookingId, isLate, arrivalTime);
      }
      toast.success(isLate ? `${student.student_name} signed in (Late)` : `${student.student_name} signed in`);
      setLateModalOpen(null);
      setLateTime('');
      applyLocalMark(student.booking_ids, 'sign_in', { is_late: isLate, arrival_time: arrivalTime });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign in');
    } finally {
      setActionLoading(null);
    }
  };

  // Sign out all bookings for a student (for master register)
  const handleSignOutAll = async (student) => {
    setActionLoading(student.primary_booking_id);
    try {
      for (const bookingId of student.booking_ids) {
        await registerAPI.signOut(bookingId);
      }
      toast.success(`${student.student_name} signed out`);
      applyLocalMark(student.booking_ids, 'sign_out');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign out');
    } finally {
      setActionLoading(null);
    }
  };

  // Undo sign in for all bookings
  const handleUndoSignInAll = async (student) => {
    setActionLoading(student.primary_booking_id);
    try {
      for (const bookingId of student.booking_ids) {
        await registerAPI.undoSignIn(bookingId);
      }
      toast.success(`Sign-in removed for ${student.student_name}`);
      applyLocalMark(student.booking_ids, 'clear');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo sign-in');
    } finally {
      setActionLoading(null);
    }
  };

  // ---- Bulk selection, staged changes & local (no-reload) updates ----
  const pendingCount = Object.keys(pendingChanges).length;

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleClassStudent = (classId, bookingId) => {
    setSelectedByClass(prev => {
      const cur = new Set(prev[classId] || []);
      if (cur.has(bookingId)) cur.delete(bookingId); else cur.add(bookingId);
      return { ...prev, [classId]: cur };
    });
  };

  // Update the master/today register rows in place (no refetch)
  const applyLocalMark = (bookingIds, action, extra = {}) => {
    const nowIso = new Date().toISOString();
    setRegisterData(prev => {
      if (!prev?.students) return prev;
      const students = prev.students.map(s => {
        if (!bookingIds.includes(s.booking_id)) return s;
        if (action === 'sign_in') {
          return { ...s, signed_in: true, signed_in_at: s.signed_in_at || nowIso, signed_out: false, signed_out_at: null, is_late: extra.is_late || s.is_late, arrival_time: extra.arrival_time || s.arrival_time };
        }
        if (action === 'sign_out') {
          return { ...s, signed_out: true, signed_out_at: s.signed_out_at || nowIso };
        }
        return { ...s, signed_in: false, signed_in_at: null, signed_out: false, signed_out_at: null, is_late: false, arrival_time: null };
      });
      return { ...prev, students };
    });
  };

  // Update a day-view class register row in place (no refetch)
  const applyLocalClassMark = (classId, bookingId, action, extra = {}) => {
    const nowIso = new Date().toISOString();
    setDayRegisterData(prev => {
      if (!prev?.classes) return prev;
      const classes = prev.classes.map(c => {
        if (c.class_id !== classId) return c;
        const students = c.students.map(s => {
          if (s.booking_id !== bookingId) return s;
          const updated = { ...s };
          if (extra.credits_remaining !== undefined) updated.credits_remaining = extra.credits_remaining;
          if (action === 'sign_in') {
            updated.present = true;
            updated.signed_in_at = extra.signed_in_at || s.signed_in_at || nowIso;
          } else if (action === 'sign_out') {
            updated.signed_out_at = extra.signed_out_at || nowIso;
          } else if (action === 'absent') {
            updated.present = false;
            updated.signed_in_at = null;
            updated.signed_out_at = null;
          } else {
            updated.present = null;
            updated.signed_in_at = null;
            updated.signed_out_at = null;
          }
          return updated;
        });
        return {
          ...c,
          students,
          present: students.filter(s => s.present === true).length,
          absent: students.filter(s => s.present === false).length,
        };
      });
      return { ...prev, classes };
    });
  };

  // Stage sign-in/out for selected rows on the master / today views
  const stageSelectedDaily = (rows, action) => {
    const updates = {};
    let staged = 0, skipped = 0;
    rows.forEach(r => {
      if (!selectedIds.has(r.key)) return;
      const eligible = action === 'sign_in' ? !r.signed_in : (r.signed_in && !r.signed_out);
      if (!eligible) { skipped++; return; }
      updates[`m:${r.key}`] = { action, booking_id: r.key, booking_ids: r.booking_ids, student_name: r.student_name };
      staged++;
    });
    if (staged) setPendingChanges(prev => ({ ...prev, ...updates }));
    setSelectedIds(new Set());
    if (staged) toast.info(`${staged} change${staged === 1 ? '' : 's'} staged — press Submit to save`);
    if (skipped) toast.warning(`${skipped} student${skipped === 1 ? '' : 's'} skipped (already in that state)`);
  };

  // Stage sign-in/out for selected rows in a day-view class register
  const stageClassSelected = (cls, visibleStudents, action) => {
    const sel = selectedByClass[cls.class_id] || new Set();
    const updates = {};
    let staged = 0, skipped = 0;
    visibleStudents.forEach(s => {
      if (!sel.has(s.booking_id)) return;
      const eligible = action === 'sign_in' ? s.present !== true : (s.present === true && !s.signed_out_at);
      if (!eligible) { skipped++; return; }
      updates[`c:${cls.class_id}|${s.booking_id}`] = { action, booking_id: s.booking_id, class_id: cls.class_id, student_name: s.student_name };
      staged++;
    });
    if (staged) setPendingChanges(prev => ({ ...prev, ...updates }));
    setSelectedByClass(prev => ({ ...prev, [cls.class_id]: new Set() }));
    if (staged) toast.info(`${staged} change${staged === 1 ? '' : 's'} staged — press Submit to save`);
    if (skipped) toast.warning(`${skipped} student${skipped === 1 ? '' : 's'} skipped (already in that state)`);
  };

  // Save all staged changes in ONE request
  const handleSubmitPending = async () => {
    if (submitting) return;
    const entries = Object.entries(pendingChanges);
    if (!entries.length) return;
    setSubmitting(true);
    setSubmitFailed(false);
    const changes = [];
    const bookingKeyMap = {};
    entries.forEach(([key, p]) => {
      if (p.class_id) {
        changes.push({ booking_id: p.booking_id, action: p.action, class_id: p.class_id });
        bookingKeyMap[`${p.class_id}|${p.booking_id}`] = key;
      } else {
        (p.booking_ids || [p.booking_id]).forEach(bid => {
          changes.push({ booking_id: bid, action: p.action });
          bookingKeyMap[`|${bid}`] = key;
        });
      }
    });
    try {
      const res = await api.post('/register/bulk-mark', {
        date: new Date().toISOString().split('T')[0],
        changes,
      });
      const results = res.data?.results || [];
      const failedKeys = new Set();
      results.forEach(r => {
        if (!r.success) {
          const key = bookingKeyMap[`${r.class_id || ''}|${r.booking_id}`];
          if (key) failedKeys.add(key);
        }
      });
      const remaining = {};
      entries.forEach(([key, p]) => {
        if (failedKeys.has(key)) { remaining[key] = p; return; }
        if (p.class_id) applyLocalClassMark(p.class_id, p.booking_id, p.action);
        else applyLocalMark(p.booking_ids || [p.booking_id], p.action);
      });
      setPendingChanges(remaining);
      if (failedKeys.size === 0) {
        toast.success(`Attendance saved — ${entries.length} student${entries.length === 1 ? '' : 's'} updated`);
      } else {
        setSubmitFailed(true);
        const firstErr = results.find(r => !r.success)?.error;
        toast.error(`${entries.length - failedKeys.size} saved, ${failedKeys.size} failed${firstErr ? `: ${firstErr}` : ''}`);
      }
    } catch (error) {
      setSubmitFailed(true);
      toast.error('Could not save attendance — your changes are kept. Tap Retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const masterRows = filteredUniqueStudents.map(s => ({ key: s.primary_booking_id, booking_ids: s.booking_ids, signed_in: s.signed_in, signed_out: s.signed_out, student_name: s.student_name }));
  const classesRows = filteredStudents.map(s => ({ key: s.booking_id, booking_ids: [s.booking_id], signed_in: s.signed_in, signed_out: s.signed_out, student_name: s.student_name }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white">
      <Navbar />
      <Toaster position="top-center" richColors />
      
      <div className={`max-w-5xl mx-auto px-4 py-8 ${pendingCount > 0 ? 'pb-36' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <ClipboardCheck className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-neutral-900">
                Class Registers
              </h1>
              <p className="text-neutral-600">
                View attendance by day
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => { fetchRegister(); if (selectedDay) fetchDayRegister(selectedDay); }} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Day Selection Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedDay(null)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  selectedDay === null
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
                data-testid="tab-today"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Today ({today})
                </div>
              </button>
              
              {availableDays.map(day => (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    selectedDay === day
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                      : day === today
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                  data-testid={`tab-${day.toLowerCase()}`}
                >
                  {day}
                </button>
              ))}
            </div>
            
            {/* Date Picker */}
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-sm text-neutral-600">Go to date:</label>
              <Input
                type="date"
                className="w-40"
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const dayName = DAYS_OF_WEEK[date.getDay()];
                  if (availableDays.includes(dayName)) {
                    setSelectedDay(dayName);
                    // Store the specific date for fetching
                    setRegisterDate(e.target.value);
                  } else {
                    toast.error(`No classes on ${dayName}`);
                  }
                }}
                data-testid="date-picker"
              />
            </div>
          </div>
          
          <p className="text-xs text-neutral-500 mt-2">
            Note: Each week's attendance resets. Previous weeks' records are stored for reports.
          </p>
        </div>

        {/* Content based on selection */}
        {selectedDay === null ? (
          /* TODAY'S REGISTER VIEW */
          <>
            {/* Date Display */}
            <div className="mb-6 text-center">
              <p className="text-neutral-600 flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                {registerData?.date ? new Date(registerData.date).toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                }) : 'Loading...'}
              </p>
            </div>

            {/* View Mode Toggle */}
            <div className="flex justify-center gap-2 mb-6">
              <button
                onClick={() => setViewMode('master')}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                  viewMode === 'master'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md'
                    : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
                data-testid="view-master"
              >
                <Users className="w-5 h-5" />
                Master Register
                <Badge className="bg-white/20 text-white ml-1">{uniqueStudents.length}</Badge>
              </button>
              <button
                onClick={() => setViewMode('classes')}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                  viewMode === 'classes'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                    : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
                data-testid="view-classes"
              >
                <ClipboardCheck className="w-5 h-5" />
                Class Registers
                <Badge className="bg-white/20 text-white ml-1">{totalCount}</Badge>
              </button>
            </div>

            {/* Stats - Clickable Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card 
                className={`cursor-pointer transition-all hover:scale-105 ${
                  statusFilter === 'signed_in' 
                    ? 'ring-2 ring-green-500 bg-green-100' 
                    : 'bg-gradient-to-br from-green-50 to-white border-green-100'
                }`}
                onClick={() => setStatusFilter(statusFilter === 'signed_in' ? 'all' : 'signed_in')}
                data-testid="filter-signed-in"
              >
                <CardContent className="p-4 text-center">
                  <LogIn className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-600">
                    {viewMode === 'master' ? uniqueSignedIn : signedInCount}
                  </p>
                  <p className="text-sm text-neutral-500">Signed In</p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all hover:scale-105 ${
                  statusFilter === 'signed_out' 
                    ? 'ring-2 ring-blue-500 bg-blue-100' 
                    : 'bg-gradient-to-br from-blue-50 to-white border-blue-100'
                }`}
                onClick={() => setStatusFilter(statusFilter === 'signed_out' ? 'all' : 'signed_out')}
                data-testid="filter-signed-out"
              >
                <CardContent className="p-4 text-center">
                  <LogOut className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-600">
                    {viewMode === 'master' ? uniqueSignedOut : signedOutCount}
                  </p>
                  <p className="text-sm text-neutral-500">Signed Out</p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all hover:scale-105 ${
                  statusFilter === 'not_arrived' 
                    ? 'ring-2 ring-yellow-500 bg-yellow-100' 
                    : 'bg-gradient-to-br from-yellow-50 to-white border-yellow-100'
                }`}
                onClick={() => setStatusFilter(statusFilter === 'not_arrived' ? 'all' : 'not_arrived')}
                data-testid="filter-not-arrived"
              >
                <CardContent className="p-4 text-center">
                  <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-yellow-600">
                    {viewMode === 'master' ? uniqueNotArrived : notArrivedCount}
                  </p>
                  <p className="text-sm text-neutral-500">Not Arrived</p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all hover:scale-105 ${
                  statusFilter === 'all' 
                    ? 'ring-2 ring-purple-500 bg-purple-100' 
                    : 'bg-gradient-to-br from-purple-50 to-white border-purple-100'
                }`}
                onClick={() => setStatusFilter('all')}
                data-testid="filter-all"
              >
                <CardContent className="p-4 text-center">
                  <Users className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-600">
                    {viewMode === 'master' ? uniqueStudents.length : totalCount}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {viewMode === 'master' ? 'Total Students' : 'Total Bookings'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <Input
                placeholder="Search by student name or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
                data-testid="search-input"
              />
            </div>

            {/* Filter Badge */}
            {statusFilter !== 'all' && (
              <div className="mb-4 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                  Filtering: {statusFilter.replace('_', ' ')}
                </Badge>
                <button 
                  onClick={() => setStatusFilter('all')}
                  className="text-sm text-purple-600 hover:underline"
                >
                  Clear filter
                </button>
              </div>
            )}

            {/* Student List - Master or Class View */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
              </div>
            ) : viewMode === 'master' ? (
              /* MASTER REGISTER - Unique students only */
              filteredUniqueStudents.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'No students match your search/filter' 
                        : 'No students expected today'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 mb-4">
                    <p className="text-sm text-green-800 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <strong>Master Register:</strong> Each student appears once. Sign them in when they arrive at the building.
                    </p>
                  </div>
                  <BulkActionBar
                    count={selectedIds.size}
                    total={filteredUniqueStudents.length}
                    allSelected={filteredUniqueStudents.length > 0 && filteredUniqueStudents.every(s => selectedIds.has(s.primary_booking_id))}
                    onToggleAll={() => {
                      const all = filteredUniqueStudents.length > 0 && filteredUniqueStudents.every(s => selectedIds.has(s.primary_booking_id));
                      setSelectedIds(all ? new Set() : new Set(filteredUniqueStudents.map(s => s.primary_booking_id)));
                    }}
                    onStageIn={() => stageSelectedDaily(masterRows, 'sign_in')}
                    onStageOut={() => stageSelectedDaily(masterRows, 'sign_out')}
                  />
                  {filteredUniqueStudents.map((student) => (
                    <Card 
                      key={student.primary_booking_id} 
                      className={`bg-white transition-all ${
                        student.signed_out 
                          ? 'border-blue-200 bg-blue-50/50' 
                          : student.signed_in 
                            ? 'border-green-200 bg-green-50/50' 
                            : 'border-neutral-200'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(student.primary_booking_id)}
                            onChange={() => toggleSelected(student.primary_booking_id)}
                            className="w-6 h-6 accent-purple-600 cursor-pointer shrink-0"
                            data-testid={`select-master-${student.primary_booking_id}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="font-semibold text-neutral-900 text-lg">{student.student_name}</h3>
                              {student.is_trial && (
                                <Badge className="bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300" data-testid={`trial-badge-${student.primary_booking_id}`}>
                                  ★ Free Trial
                                </Badge>
                              )}
                              {student.is_payg && (
                                <Badge
                                  className={(student.credits_remaining ?? 0) > 0
                                    ? 'bg-amber-100 text-amber-800 border-amber-300'
                                    : 'bg-red-100 text-red-700 border-red-300'}
                                  data-testid={`payg-badge-${student.primary_booking_id}`}
                                >
                                  {(student.credits_remaining ?? 0) > 0
                                    ? <>🎟 PAYG · {student.credits_remaining} credit{student.credits_remaining === 1 ? '' : 's'} left</>
                                    : <>🎟 PAYG · 0 credits — top up needed</>}
                                </Badge>
                              )}
                              {student.class_names.length > 1 && (
                                <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                                  {student.class_names.length} classes today
                                </Badge>
                              )}
                              {student.is_absent_reported && (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                  <CalendarX className="w-3 h-3 mr-1" />
                                  Absence Reported
                                </Badge>
                              )}
                              {student.signed_out ? (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                  <LogOut className="w-3 h-3 mr-1" />
                                  Signed Out {formatTime(student.signed_out_at)}
                                </Badge>
                              ) : student.signed_in ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Signed In {formatTime(student.signed_in_at)}
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Not Arrived
                                </Badge>
                              )}
                              {student.is_late && (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Late {student.arrival_time}
                                </Badge>
                              )}
                              {pendingChanges[`m:${student.primary_booking_id || student.booking_id}`] && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`pending-badge-${student.primary_booking_id || student.booking_id}`}>
                                  Pending {pendingChanges[`m:${student.primary_booking_id || student.booking_id}`].action === 'sign_in' ? 'Sign In' : 'Sign Out'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {student.class_names.map((cls, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-white">
                                  {cls}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 ml-auto flex-wrap">
                            {!student.signed_in ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleSignInAll(student)}
                                  disabled={actionLoading === student.primary_booking_id}
                                  className="bg-green-500 hover:bg-green-600"
                                  data-testid={`sign-in-${student.primary_booking_id}`}
                                >
                                  {actionLoading === student.primary_booking_id ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <LogIn className="w-4 h-4 mr-1" />
                                      Sign In
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setLateModalOpen(student.primary_booking_id)}
                                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Late
                                </Button>
                              </>
                            ) : !student.signed_out ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleSignOutAll(student)}
                                  disabled={actionLoading === student.primary_booking_id}
                                  className="bg-blue-500 hover:bg-blue-600"
                                  data-testid={`sign-out-${student.primary_booking_id}`}
                                >
                                  {actionLoading === student.primary_booking_id ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <LogOut className="w-4 h-4 mr-1" />
                                      Sign Out
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUndoSignInAll(student)}
                                  disabled={actionLoading === student.primary_booking_id}
                                  className="text-neutral-600"
                                >
                                  <Undo2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUndoSignInAll(student)}
                                disabled={actionLoading === student.primary_booking_id}
                                className="text-neutral-600"
                              >
                                <Undo2 className="w-4 h-4 mr-1" />
                                Reset
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Late Modal Inline */}
                        {lateModalOpen === student.primary_booking_id && (
                          <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-sm text-orange-700 mb-2">Enter arrival time:</p>
                            <div className="flex gap-2">
                              <Input
                                type="time"
                                value={lateTime}
                                onChange={(e) => setLateTime(e.target.value)}
                                className="w-32"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleSignInAll(student, true, lateTime)}
                                disabled={actionLoading === student.primary_booking_id}
                                className="bg-orange-500 hover:bg-orange-600"
                              >
                                {actionLoading === student.primary_booking_id ? (
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                  'Sign In Late'
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setLateModalOpen(null); setLateTime(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            ) : (
              /* CLASS REGISTERS VIEW - Each booking separately */
              filteredStudents.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'No students match your search/filter' 
                        : 'No students expected today'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200 mb-4">
                    <p className="text-sm text-purple-800 flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      <strong>Class Registers:</strong> Students appear for each class they're enrolled in. Use for per-class attendance.
                    </p>
                  </div>
                  <BulkActionBar
                    count={selectedIds.size}
                    total={filteredStudents.length}
                    allSelected={filteredStudents.length > 0 && filteredStudents.every(s => selectedIds.has(s.booking_id))}
                    onToggleAll={() => {
                      const all = filteredStudents.length > 0 && filteredStudents.every(s => selectedIds.has(s.booking_id));
                      setSelectedIds(all ? new Set() : new Set(filteredStudents.map(s => s.booking_id)));
                    }}
                    onStageIn={() => stageSelectedDaily(classesRows, 'sign_in')}
                    onStageOut={() => stageSelectedDaily(classesRows, 'sign_out')}
                  />
                  {filteredStudents.map((student) => (
                    <Card 
                      key={student.booking_id} 
                      className={`bg-white transition-all ${
                        student.signed_out 
                          ? 'border-blue-200 bg-blue-50/50' 
                          : student.signed_in 
                            ? 'border-green-200 bg-green-50/50' 
                            : 'border-neutral-200'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(student.booking_id)}
                            onChange={() => toggleSelected(student.booking_id)}
                            className="w-6 h-6 accent-purple-600 cursor-pointer shrink-0"
                            data-testid={`select-booking-${student.booking_id}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold text-neutral-900">{student.student_name}</h3>
                              {student.is_trial && (
                                <Badge className="bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300" data-testid={`trial-badge-class-${student.booking_id}`}>
                                  ★ Free Trial
                                </Badge>
                              )}
                              {student.is_payg && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`payg-badge-class-${student.booking_id}`}>
                                  🎟 PAYG · {student.credits_remaining ?? 0} credits left
                                </Badge>
                              )}
                              {student.is_absent_reported && (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                  <CalendarX className="w-3 h-3 mr-1" />
                                  Absence Reported
                                </Badge>
                              )}
                              {student.signed_out ? (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                  <LogOut className="w-3 h-3 mr-1" />
                                  Signed Out {formatTime(student.signed_out_at)}
                                </Badge>
                              ) : student.signed_in ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Signed In {formatTime(student.signed_in_at)}
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Not Arrived
                                </Badge>
                              )}
                              {student.is_late && (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Late {student.arrival_time}
                                </Badge>
                              )}
                              {pendingChanges[`m:${student.primary_booking_id || student.booking_id}`] && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`pending-badge-${student.primary_booking_id || student.booking_id}`}>
                                  Pending {pendingChanges[`m:${student.primary_booking_id || student.booking_id}`].action === 'sign_in' ? 'Sign In' : 'Sign Out'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {student.class_names.map((cls, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-white">
                                  {cls}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 ml-auto flex-wrap">
                            {!student.signed_in ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleSignIn(student.booking_id)}
                                  disabled={actionLoading === student.booking_id}
                                  className="bg-green-500 hover:bg-green-600"
                                  data-testid={`sign-in-${student.booking_id}`}
                                >
                                  {actionLoading === student.booking_id ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <LogIn className="w-4 h-4 mr-1" />
                                      Sign In
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setLateModalOpen(student.booking_id)}
                                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Late
                                </Button>
                              </>
                            ) : !student.signed_out ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleSignOut(student.booking_id)}
                                  disabled={actionLoading === student.booking_id}
                                  className="bg-blue-500 hover:bg-blue-600"
                                  data-testid={`sign-out-${student.booking_id}`}
                                >
                                  {actionLoading === student.booking_id ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <LogOut className="w-4 h-4 mr-1" />
                                      Sign Out
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUndoSignIn(student.booking_id)}
                                  disabled={actionLoading === student.booking_id}
                                  className="text-neutral-600"
                                >
                                  <Undo2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUndoSignIn(student.booking_id)}
                                disabled={actionLoading === student.booking_id}
                                className="text-neutral-600"
                              >
                                <Undo2 className="w-4 h-4 mr-1" />
                                Reset
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Late Modal Inline */}
                        {lateModalOpen === student.booking_id && (
                          <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <p className="text-sm text-orange-700 mb-2">Enter arrival time:</p>
                            <div className="flex gap-2">
                              <Input
                                type="time"
                                value={lateTime}
                                onChange={(e) => setLateTime(e.target.value)}
                                className="w-32"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleSignInLate(student.booking_id)}
                                disabled={actionLoading === student.booking_id}
                                className="bg-orange-500 hover:bg-orange-600"
                              >
                                {actionLoading === student.booking_id ? (
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                  'Sign In Late'
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setLateModalOpen(null); setLateTime(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            )}
            
            {/* Close Day Button */}
            {uniqueSignedIn > 0 && uniqueSignedOut < uniqueStudents.length && !dayIsClosed && (
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900">End of Day</h3>
                    <p className="text-sm text-neutral-600">
                      {uniqueSignedIn} students still signed in. Close the day to sign everyone out.
                    </p>
                  </div>
                  <Button
                    onClick={handleCloseDay}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    data-testid="close-day-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Close Day
                  </Button>
                </div>
              </div>
            )}
            
            {dayIsClosed && (
              <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200 text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-green-700">Day Complete ✓</p>
                <p className="text-sm text-green-600">All students accounted for - register automatically closed.</p>
              </div>
            )}
          </>
        ) : (
          /* DAY-SPECIFIC REGISTER VIEW */
          <>
            <div className="mb-6 text-center">
              <h2 className="text-xl font-bold text-neutral-900">{selectedDay} Classes</h2>
              <p className="text-neutral-600">
                {dayRegisterData?.total_classes || 0} classes scheduled
              </p>
            </div>

            {/* Day Stats */}
            {dayRegisterData?.classes && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
                  <CardContent className="p-4 text-center">
                    <Users className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-purple-600">
                      {/* Count unique students across all classes */}
                      {new Set(dayRegisterData.classes.flatMap(c => c.students.map(s => s.student_name?.toLowerCase().trim()))).size}
                    </p>
                    <p className="text-xs text-neutral-500">Unique Students</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-green-600">
                      {dayRegisterData.classes.reduce((sum, c) => sum + c.present, 0)}
                    </p>
                    <p className="text-xs text-neutral-500">Present</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
                  <CardContent className="p-4 text-center">
                    <XCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-red-600">
                      {dayRegisterData.classes.reduce((sum, c) => sum + c.absent, 0)}
                    </p>
                    <p className="text-xs text-neutral-500">Absent</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-100">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-yellow-600">
                      {dayRegisterData.classes.reduce((sum, c) => sum + (c.total_enrolled - c.present - c.absent), 0)}
                    </p>
                    <p className="text-xs text-neutral-500">Not Marked</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
              </div>
            ) : !dayRegisterData?.classes?.length ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-500">No classes scheduled for {selectedDay}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {dayRegisterData.classes.map((cls) => (
                  <Card 
                    key={cls.class_id} 
                    className="bg-white overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedClass(expandedClass === cls.class_id ? null : cls.class_id)}
                      className="w-full p-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
                      data-testid={`class-${cls.class_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold">
                          {cls.start_time?.substring(0, 5) || '?'}
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-neutral-900">{cls.class_name}</h3>
                          <p className="text-sm text-neutral-500">
                            {cls.start_time} - {cls.end_time} {cls.room && `• ${cls.room}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          {/* Show deduplicated student count */}
                          <p className="text-2xl font-bold text-purple-600">
                            {new Set(cls.students.map(s => s.student_name?.toLowerCase().trim()).filter(Boolean)).size}
                          </p>
                          <p className="text-xs text-neutral-500">students</p>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-neutral-400 transition-transform ${expandedClass === cls.class_id ? 'rotate-90' : ''}`} />
                      </div>
                    </button>
                    
                    {/* Expanded Student List */}
                    {expandedClass === cls.class_id && (
                      <div className="border-t border-neutral-100 bg-neutral-50 p-4">
                        {cls.students.length === 0 ? (
                          <p className="text-neutral-500 text-sm text-center py-2">No students enrolled</p>
                        ) : (
                          <div className="space-y-2">
                            {(() => {
                              const seen = new Set();
                              const visibleStudents = cls.students.filter(student => {
                                const key = student.student_name?.toLowerCase().trim();
                                if (!key || seen.has(key)) return false;
                                seen.add(key);
                                return true;
                              });
                              const clsSelected = selectedByClass[cls.class_id] || new Set();
                              const allSelected = visibleStudents.length > 0 && visibleStudents.every(s => clsSelected.has(s.booking_id));
                              return (<>
                              <div className="flex flex-wrap items-center gap-3 p-2 bg-white rounded-lg border border-neutral-200" data-testid={`bulk-bar-${cls.class_id}`}>
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={() => setSelectedByClass(prev => ({
                                      ...prev,
                                      [cls.class_id]: allSelected ? new Set() : new Set(visibleStudents.map(s => s.booking_id))
                                    }))}
                                    className="w-6 h-6 accent-purple-600 cursor-pointer"
                                    data-testid={`select-all-${cls.class_id}`}
                                  />
                                  <span className="text-sm font-medium text-neutral-700">Select All ({visibleStudents.length})</span>
                                </label>
                                {clsSelected.size > 0 && (
                                  <div className="flex gap-2 ml-auto flex-wrap">
                                    <Button size="sm" onClick={() => stageClassSelected(cls, visibleStudents, 'sign_in')} className="bg-green-500 hover:bg-green-600" data-testid={`bulk-sign-in-${cls.class_id}`}>
                                      <LogIn className="w-4 h-4 mr-1" /> Sign In Selected ({clsSelected.size})
                                    </Button>
                                    <Button size="sm" onClick={() => stageClassSelected(cls, visibleStudents, 'sign_out')} className="bg-blue-500 hover:bg-blue-600" data-testid={`bulk-sign-out-${cls.class_id}`}>
                                      <LogOut className="w-4 h-4 mr-1" /> Sign Out Selected
                                    </Button>
                                  </div>
                                )}
                              </div>
                              {visibleStudents.map((student, idx) => (
                              <div 
                                key={idx}
                                className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white rounded-lg border border-neutral-200"
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={(selectedByClass[cls.class_id] || new Set()).has(student.booking_id)}
                                    onChange={() => toggleClassStudent(cls.class_id, student.booking_id)}
                                    className="w-6 h-6 accent-purple-600 cursor-pointer shrink-0"
                                    data-testid={`select-student-${student.booking_id}`}
                                  />
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    student.present === true ? 'bg-green-100' :
                                    student.present === false ? 'bg-red-100' : 'bg-purple-100'
                                  }`}>
                                    <span className={`font-medium text-sm ${
                                      student.present === true ? 'text-green-600' :
                                      student.present === false ? 'text-red-600' : 'text-purple-600'
                                    }`}>
                                      {student.student_name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-neutral-900">{student.student_name}</span>
                                    {student.is_trial && (
                                      <Badge className="ml-2 bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300 text-xs" data-testid={`trial-badge-day-${student.booking_id}`}>
                                        ★ Free Trial
                                      </Badge>
                                    )}
                                    {student.is_payg && (
                                      <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-xs" data-testid={`payg-badge-day-${student.booking_id}`}>
                                        🎟 PAYG · {student.credits_remaining ?? 0} left
                                      </Badge>
                                    )}
                                    {student.payment_status && student.payment_status !== 'paid' && !student.is_trial && !student.is_payg && (
                                      <Badge className="ml-2 bg-yellow-100 text-yellow-700 text-xs">
                                        Payment Pending
                                      </Badge>
                                    )}
                                    {student.is_absent_reported && (
                                      <Badge className="ml-2 bg-orange-100 text-orange-700 text-xs">
                                        Absence Reported
                                      </Badge>
                                    )}
                                    {pendingChanges[`c:${cls.class_id}|${student.booking_id}`] && (
                                      <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-xs" data-testid={`pending-badge-day-${student.booking_id}`}>
                                        Pending {pendingChanges[`c:${cls.class_id}|${student.booking_id}`].action === 'sign_in' ? 'Sign In' : 'Sign Out'}
                                      </Badge>
                                    )}
                                    {(student.signed_in_at || student.signed_out_at) && (
                                      <p className="text-xs text-neutral-500 mt-0.5" data-testid={`times-${student.booking_id}`}>
                                        {student.signed_in_at && <span className="text-green-600 font-medium">In {fmtTime(student.signed_in_at)}</span>}
                                        {student.signed_in_at && student.signed_out_at && <span> · </span>}
                                        {student.signed_out_at && <span className="text-blue-600 font-medium">Out {fmtTime(student.signed_out_at)}</span>}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Attendance Buttons */}
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                  <Button
                                    size="sm"
                                    variant={student.present === true ? "default" : "outline"}
                                    onClick={() => handleMarkAttendance(cls.class_id, student.booking_id, 'sign_in')}
                                    disabled={markingAttendance === `${cls.class_id}-${student.booking_id}` || student.present === true}
                                    className={student.present === true 
                                      ? "bg-green-500 hover:bg-green-600" 
                                      : "border-green-300 text-green-600 hover:bg-green-50"
                                    }
                                    data-testid={`signin-btn-${student.booking_id}`}
                                  >
                                    {markingAttendance === `${cls.class_id}-${student.booking_id}` ? (
                                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <>
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        {student.present === true ? 'Signed In' : 'Sign In'}
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={student.signed_out_at ? "default" : "outline"}
                                    onClick={() => handleMarkAttendance(cls.class_id, student.booking_id, 'sign_out')}
                                    disabled={markingAttendance === `${cls.class_id}-${student.booking_id}` || student.present !== true || !!student.signed_out_at}
                                    className={student.signed_out_at
                                      ? "bg-blue-500 hover:bg-blue-600"
                                      : "border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-40"
                                    }
                                    data-testid={`signout-btn-${student.booking_id}`}
                                  >
                                    <LogOut className="w-4 h-4 mr-1" />
                                    {student.signed_out_at ? 'Signed Out' : 'Sign Out'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={student.present === false ? "default" : "outline"}
                                    onClick={() => handleMarkAttendance(cls.class_id, student.booking_id, 'absent')}
                                    disabled={markingAttendance === `${cls.class_id}-${student.booking_id}`}
                                    className={student.present === false 
                                      ? "bg-red-500 hover:bg-red-600" 
                                      : "border-red-300 text-red-600 hover:bg-red-50"
                                    }
                                    data-testid={`absent-btn-${student.booking_id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Absent
                                  </Button>
                                </div>
                              </div>
                              ))}
                              </>);
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky unsaved-changes submit bar */}
      {pendingCount > 0 && (
        <div className="fixed bottom-16 md:bottom-0 inset-x-0 z-[60] bg-white/95 backdrop-blur border-t border-neutral-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" data-testid="submit-bar">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-sm px-3 py-1" data-testid="pending-count-badge">
                {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
              </Badge>
              {submitFailed && (
                <span className="text-sm text-red-600 font-medium" data-testid="submit-failed-msg">Save failed — changes kept</span>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setPendingChanges({}); setSubmitFailed(false); }}
                disabled={submitting}
                data-testid="discard-changes-btn"
              >
                Discard
              </Button>
              <Button
                onClick={handleSubmitPending}
                disabled={submitting}
                className={submitFailed
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'}
                data-testid="submit-attendance-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : submitFailed ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry Save
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit ({pendingCount})
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
